import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
} from 'react-native';
import { useTheme, useRoute } from '@react-navigation/native';
import { getCommonStyles } from '@utils';
  import { DeleteModal, Icon, Screen } from '@components';
  import ContactsListEdit from '../components/ContactsListEdit';
import { EditInput } from '../components/EditInput';
import { Theme } from '@theme';
import { useDeleteCategory, useGetCategoryById, useScreen, useUpdateCategory } from '@hooks';
import CloseButton from '../components/CloseButton';
import { useSafeNavigation } from '../../../navigation/web/useSafeNavigation';
import { PhotoCategory } from './AddCategory';
import { useLanguage } from '@context';

interface EditCategoryProps {
  categoryId?: string;
  onBack?: () => void;
  onDeleted?: () => void;
}

export const EditCategory: React.FC<EditCategoryProps> = ({ categoryId: propCategoryId, onBack, onDeleted }) => {  const theme = useTheme()
  const { t } = useLanguage()
  const {isMobile, isDesktop} = useScreen();
  const styles = getStyle(theme)
  const commonStyles = getCommonStyles(theme)
  const navigation = useSafeNavigation();
  const route: any = useRoute()
  const categoryId = propCategoryId || route.params?.categoryId

  if(!categoryId) return

  const { data: category } = useGetCategoryById(categoryId);
  const { mutate: updateCategory, isPending: isSaving } = useUpdateCategory();
  const { mutate: deleteCategory, isPending: isDeleting } = useDeleteCategory();
  //const subcategory = routeSubcategory; // For now, subcategory only comes from route
  //const isSubcategory = !!subcategory

  const [selectedContactIds, setSelectedContactIds] = useState<string[]>(/*!!subcategory? (subcategory?.contacts || []) : */ (category?.contactAccountIds || []));
  const [categoryTitle, setCategoryTitle] = useState(/*!!subcategory? (subcategory?.name || '') :*/ (category?.name || ''));
  const [hasModified, setHasModified] = useState(false);
  // Only a photo picked on this screen. The photo the category already has is
  // an address on the server: sent back as a file, the whole save was refused.
  const [newPhoto, setNewPhoto] = useState<string | undefined>(undefined);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);

  // Update state when categoryId changes
  useEffect(() => {
    if (category) {
      setSelectedContactIds(/*subcategory ? (subcategory.contacts || []) :*/ (category.contactAccountIds || []));
      setCategoryTitle(/*subcategory ? (subcategory.name || '') :*/ (category.name || ''));
    } else {
      setSelectedContactIds([]);
      setCategoryTitle('');
    }
  }, [category/*, subcategory, propCategoryId*/]);

  useEffect(() => {
    setHasModified(categoryTitle !== category?.name || selectedContactIds !== category?.contactAccountIds || !!newPhoto);
  }, [categoryTitle, selectedContactIds, newPhoto]);

  const handleBack = () => {
    if(onBack)
      onBack()
    else
      navigation?.goBack();
  };

  const handleAddPhoto = (uri: string) => {
    setNewPhoto(uri)
  };

  const handleUpdate = () => {

    /*if(isSubcategory){
      updateSubcategory(category!.id, subcategory.id, {
        name: categoryTitle,
        contacts: selectedContacts
      })
    }
    else {*/
    updateCategory({
      id: category!.id,
      name: categoryTitle,
      contactAccountIds: selectedContactIds,
      avatar: newPhoto ? { uri: newPhoto } : undefined,
    }, {
      // The screen closes once the server has accepted the save: a refused
      // one leaves it open with what was typed.
      onSuccess: () => {
        setHasModified(false);
        handleBack();
      },
    })
    //}
  };

  const handleDelete = () => {
    setIsDeleteModalVisible(true)
  }

  const handleDeleteConfirm = () => {
    deleteCategory({ id: category!.id }, {
      // Once the server has deleted it; a refused deletion leaves the screen open.
      onSuccess: () => {
        // Clear the selected id in the two-pane parent so the detail panel resets
        // to its empty state and no GET /categories/:id fires for the deleted id.
        if (onDeleted) {
          onDeleted()
        } else {
          handleBack()
        }
      },
    })
  }

  return (
    <Screen>
      <View style={styles.header}>
        {isMobile && <TouchableOpacity onPress={handleBack}>
          <Icon name="arrowBack" size={25} color={theme.colors.black} />
        </TouchableOpacity>}
        <Text style={styles.headerTitle}>{t('editCategory.title')}</Text>
        <TouchableOpacity onPress={handleDelete} disabled={isDeleting}>
          <Icon name="trash" size={20} color={theme.colors.error} />
        </TouchableOpacity>
      </View>

      <View style={[styles.content, isDesktop && commonStyles.searchContainerNonMobile, isDesktop && commonStyles.alignStart, isDesktop && commonStyles.gapXxxl]}>
        <View style={!isMobile && [commonStyles.paddingTop, commonStyles.searchContainerMaxWidth]}>
          {/* Photo de la catégorie */}
          <PhotoCategory isSubcategory={categoryId} isEdit photo={newPhoto ?? category?.avatarUrl ?? undefined} onAddPhoto={handleAddPhoto} />
          {/* Titre de la catégorie */}
          <EditInput placeholder={t('categoriesScreen.categoryTitlePlaceholder')} value={categoryTitle} onChangeText={setCategoryTitle} />
        </View>

        <View style={[commonStyles.flex, commonStyles.fullHeight]}>
          <ContactsListEdit isEdit={(category?.contactAccountIds?.length || 0) > 0} title={t('categoriesScreen.categoryContacts')} selectedContactIds={selectedContactIds} setSelectedContactIds={setSelectedContactIds}/>
        </View>
      </View> 
    
      {/* Bouton Mettre à jour */}
      <View style={[styles.footer, !isMobile && commonStyles.rowBetween]}>
        {!isMobile && <CloseButton onPress={handleBack} />}
        <TouchableOpacity
          style={[styles.saveButton, (!categoryTitle.trim() || !hasModified || isSaving || isDeleting) && styles.saveButtonDisabled]}
          onPress={handleUpdate}
          disabled={!categoryTitle.trim() || !hasModified || isSaving || isDeleting}
        >
          <Text style={styles.saveButtonText}>
            {t('common.save')}
          </Text>
        </TouchableOpacity>
      </View>
      <DeleteModal isVisible={isDeleteModalVisible} setIsVisible={setIsDeleteModalVisible} title={t('editCategory.deleteConfirm')} onDelete={handleDeleteConfirm} />
    </Screen>
  );
};

const getStyle = (theme: Theme) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F2F2F7',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingBottom: theme.spacing.md,
    backgroundColor: 'white',
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.lightGrey,
  },
  headerTitle: {
    fontSize: theme.fontSize.large,
    fontWeight: 'bold',
    color: theme.colors.text,
  },
  placeholder: {
    width: 30,
  },
  content: {
    flex: 1,
    padding: theme.spacing.md,
  },
  categoryImage: {
    width: '100%',
    height: 150,
  },
  titleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'white',
    borderRadius: 25,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginBottom: 16,
  },
  titleInput: {
    flex: 1,
    fontSize: 16,
    color: '#000',
  },
  contactsSection: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
  },
  contactsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  contactsInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  contactsTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#000',
    marginLeft: 8,
  },
  contactsCount: {
    fontSize: 13,
    color: '#8E8E93',
  },
  categoryContactsList: {
    marginBottom: 16,
  },
  categoryContactItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
  },
  categoryContactAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    marginRight: 12,
  },
  categoryContactName: {
    flex: 1,
    fontSize: 16,
    color: theme.colors.text,
  },
  addContactsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderWidth: 1,
    borderColor: '#007AFF',
    borderRadius: 8,
    borderStyle: 'dashed',
  },
  addContactsText: {
    fontSize: 16,
    color: '#007AFF',
    marginLeft: 8,
  },
  footer: {
    backgroundColor: 'white',
    paddingBottom: theme.spacing.md,
    paddingHorizontal: theme.spacing.md,
  },
  saveButton: {
    borderRadius: 25,
    paddingVertical: theme.spacing.sm,
    paddingHorizontal: theme.spacing.xxxl,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: theme.colors.primary,
  },
  saveButtonDisabled: {
    opacity: 0.5,
  },
  saveButtonText: {
    fontSize: theme.fontSize.small,
    color: theme.colors.primary,
  },
  updateButton: {
    backgroundColor: theme.colors.primary,
    borderRadius: 25,
    paddingVertical: 16,
    alignItems: 'center',
  },
  updateButtonDisabled: {
    backgroundColor: theme.colors.grey,
  },
  updateButtonText: {
    fontSize: 17,
    fontWeight: '600',
    color: 'white',
  },
  updateButtonTextDisabled: {
    color: theme.colors.lightGrey,
  },
});

export default EditCategory; 